import {
  Table,
  TableContainer,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Box,
  SkeletonText,
  Button,
} from "@chakra-ui/react";
import { format } from "date-fns";

import DeleteIcon from "@/assets/DeleteIcon";
import EditIcon from "@/assets/EditIcon";
import { formatCurrency } from "@/utils/currency";
import SentIcon from "@/assets/SentIcon";
import DraftIcon from "@/assets/DraftIcon";
import ApprovedIcon from "@/assets/ApprovedIcon";
import RejectIcon from "@/assets/RejectIcon";
import UnpaidIcon from "@/assets/UnpaidIcon";
import PaidIcon from "@/assets/PaidIcon";
import Pagination from "../Layouts/Pagination";
import ButtonConfirm from "@/assets/ButtonConfirm";
import ReceivedIcon from "@/assets/ReceivedIcon";
import {
  COLORS,
  CURRENCY_TO_LOCALE_STRING,
  INVOICE_STATUS,
  MONTHS_OPTION,
  PAYMENT_STATUS,
  ROLES,
  UPLOAD_TYPE,
} from "@/common/constants";
import CompletedIcon from "@/assets/CompletedIcon";
import PendingIcon from "@/assets/PendingIcon";
import NewIcon from "@/assets/NewIcon";
import FileIcon from "@/assets/FileIcon";
import { useNavigate } from "react-router-dom";
import ButtonView from "@/assets/ButtonView";
import { TableProps } from "@/types/table.interface";
import { SireReviewInvoice } from "@/types/sireReviewInvoice.interface";

interface TableInvoiceSireReviewInspectorProps<T> extends TableProps<T> {
  role: string;
  handleEdit: (id: string) => void;
  handleDelete: (id: string) => void;
  handlePay: (id: string) => void;
  handleDownloadZip: (id: string, uploadType: number) => void;
  handleConfirmInvoice: (id: string) => void;
  handleConfirmPayment: (id: string) => void;
}

const ConfirmationColumn = (role: string) => {
  switch (role) {
    case ROLES.INSPECTING_COMPANY:
      return (
        <Th
          color={COLORS.TABLE_HEADER.FOREGROUND}
          backgroundColor={COLORS.TABLE_HEADER.BACKGROUND}
          textTransform="none"
          fontSize={14}
          textAlign="center"
        >
          Payment Confirmation
        </Th>
      );
    case ROLES.SUPER_ADMIN:
      return (
        <Th
          color={COLORS.TABLE_HEADER.FOREGROUND}
          backgroundColor={COLORS.TABLE_HEADER.BACKGROUND}
          textTransform="none"
          fontSize={14}
          textAlign="center"
        >
          Invoice Confirmation
        </Th>
      );
    case ROLES.FINANCE:
      return (
        <Th
          color={COLORS.TABLE_HEADER.FOREGROUND}
          backgroundColor={COLORS.TABLE_HEADER.BACKGROUND}
          textTransform="none"
          fontSize={14}
          textAlign="center"
        >
          Payment
        </Th>
      );
    default:
      return null;
  }
};

const InvoiceStatus = (status: number, role: string) => {
  switch (status) {
    case INVOICE_STATUS.DRAFT:
      return <DraftIcon />;
    case INVOICE_STATUS.APPROVED:
      return <ApprovedIcon />;
    case INVOICE_STATUS.IN_PROGRESS:
      return role === ROLES.INSPECTING_COMPANY ? <SentIcon /> : <NewIcon />;
    case INVOICE_STATUS.REJECTED:
      return <RejectIcon />;
    default:
      return null;
  }
};

const PaymentStatus = (status: number, role: string) => {
  switch (status) {
    case PAYMENT_STATUS.UNPAID:
      return <UnpaidIcon />;
    case PAYMENT_STATUS.PAID:
      return <PaidIcon />;
    case PAYMENT_STATUS.PENDING:
      return <PendingIcon />;
    case PAYMENT_STATUS.REJECTED:
      return <RejectIcon />;
    case PAYMENT_STATUS.COMPLETED:
      return role === ROLES.INSPECTING_COMPANY ? (
        <ReceivedIcon />
      ) : (
        <CompletedIcon />
      );
    default:
      return null;
  }
};

const TableInvoiceSireReviewInspector = ({
  role,
  dataList,
  isLoading,
  handleEdit,
  handleDelete,
  currentPageSize,
  handleChangePageSize,
  handleChangePageNumber,
  handlePay,
  currentPageNumber,
  totalPageNumber,
  hasNextPage,
  handleDownloadZip,
  handleConfirmInvoice,
  handleConfirmPayment,
}: TableInvoiceSireReviewInspectorProps<SireReviewInvoice[]>) => {
  const navigate = useNavigate();

  const handleDetails = (id: string) => {
    navigate("/manage-invoices/sire-inspector/reviews", {
      state: { id, isRequestor: false },
    });
  };

  const hasReviewerColumn =
    role === ROLES.SUPER_ADMIN || role === ROLES.FINANCE;

  const NUM_COLUMNS = hasReviewerColumn ? 13 : 12;
  return (
    <TableContainer bgColor="#fff" borderRadius="13">
      <Box overflowX="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              {hasReviewerColumn && (
                <Th
                  color={COLORS.TABLE_HEADER.FOREGROUND}
                  backgroundColor={COLORS.TABLE_HEADER.BACKGROUND}
                  textTransform="none"
                  fontSize={14}
                >
                  Reviewer
                </Th>
              )}
              <Th
                color={COLORS.TABLE_HEADER.FOREGROUND}
                backgroundColor={COLORS.TABLE_HEADER.BACKGROUND}
                textTransform="none"
                fontSize={14}
              >
                Invoice ID
              </Th>
              <Th
                color={COLORS.TABLE_HEADER.FOREGROUND}
                backgroundColor={COLORS.TABLE_HEADER.BACKGROUND}
                textTransform="none"
                fontSize={14}
              >
                Review Month
              </Th>
              <Th
                color={COLORS.TABLE_HEADER.FOREGROUND}
                backgroundColor={COLORS.TABLE_HEADER.BACKGROUND}
                textTransform="none"
                fontSize={14}
              >
                Invoice Date
              </Th>
              <Th
                color={COLORS.TABLE_HEADER.FOREGROUND}
                backgroundColor={COLORS.TABLE_HEADER.BACKGROUND}
                textTransform="none"
                fontSize={14}
              >
                Total Reviews
              </Th>
              <Th
                color={COLORS.TABLE_HEADER.FOREGROUND}
                backgroundColor={COLORS.TABLE_HEADER.BACKGROUND}
                textTransform="none"
                fontSize={14}
              >
                Review Cost
              </Th>
              <Th
                color={COLORS.TABLE_HEADER.FOREGROUND}
                backgroundColor={COLORS.TABLE_HEADER.BACKGROUND}
                textTransform="none"
                fontSize={14}
              >
                Total Amount
              </Th>
              <Th
                color={COLORS.TABLE_HEADER.FOREGROUND}
                backgroundColor={COLORS.TABLE_HEADER.BACKGROUND}
                textTransform="none"
                fontSize={14}
              >
                Invoice File
              </Th>
              <Th
                color={COLORS.TABLE_HEADER.FOREGROUND}
                backgroundColor={COLORS.TABLE_HEADER.BACKGROUND}
                textTransform="none"
                fontSize={14}
                textAlign="center"
              >
                Invoice Status
              </Th>
              <Th
                color={COLORS.TABLE_HEADER.FOREGROUND}
                backgroundColor={COLORS.TABLE_HEADER.BACKGROUND}
                textTransform="none"
                fontSize={14}
                textAlign="center"
              >
                Payment Status
              </Th>
              {ConfirmationColumn(role)}
              <Th
                color={COLORS.TABLE_HEADER.FOREGROUND}
                backgroundColor={COLORS.TABLE_HEADER.BACKGROUND}
                textTransform="none"
                fontSize={14}
              >
                Note
              </Th>
              <Th
                color={COLORS.TABLE_HEADER.FOREGROUND}
                backgroundColor={COLORS.TABLE_HEADER.BACKGROUND}
                textTransform="none"
                fontSize={14}
                textAlign="center"
              >
                Action
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && (
              <Tr>
                <Td colSpan={NUM_COLUMNS}>
                  <SkeletonText
                    mt="4"
                    noOfLines={4}
                    spacing="4"
                    skeletonHeight="3"
                  />
                </Td>
              </Tr>
            )}
            {!isLoading && (!dataList || dataList.length === 0) && (
              <Tr>
                <Td colSpan={NUM_COLUMNS} textAlign="center">
                  Data not found
                </Td>
              </Tr>
            )}
            {!isLoading &&
              dataList?.map((item) => {
                return (
                  <Tr key={item.id}>
                    {hasReviewerColumn && (
                      <Td>{item.inspectionCompanyName || "-"}</Td>
                    )}
                    <Td>{item.invoiceNumber}</Td>
                    <Td>
                      {MONTHS_OPTION[item.reviewMonth] + " " + item.reviewYear}
                    </Td>
                    <Td>
                      {item.invoiceDate
                        ? format(item.invoiceDate, "dd-MM-yyyy")
                        : "-"}
                    </Td>
                    <Td>
                      <Button
                        bgColor="#079455"
                        borderRadius="full"
                        color="white"
                        height="25px"
                        onClick={() => handleDetails(item.id)}
                      >
                        {item.totalReview} - Detail
                      </Button>
                    </Td>
                    <Td>
                      {item.costReview
                        ? formatCurrency(
                            item.costReview,
                            item.currency,
                            CURRENCY_TO_LOCALE_STRING[item.currency],
                          )
                        : "-"}
                    </Td>
                    <Td>
                      {item.totalAmount
                        ? formatCurrency(
                            item.totalAmount,
                            item.currency,
                            CURRENCY_TO_LOCALE_STRING[item.currency],
                          )
                        : "-"}
                    </Td>
                    <Td>
                      <div
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        {item.invoiceDownloadId ? (
                          <FileIcon
                            onClick={() =>
                              handleDownloadZip(
                                item.id,
                                UPLOAD_TYPE.SireReviewInvoice,
                              )
                            }
                            style={{ cursor: "pointer" }}
                          />
                        ) : (
                          <p>-</p>
                        )}
                      </div>
                    </Td>
                    <Td textAlign="center">
                      <div
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        {InvoiceStatus(item.invoiceStatus, role)}
                      </div>
                    </Td>
                    <Td textAlign="center">
                      <div
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        {PaymentStatus(item.paymentStatus, role)}
                      </div>
                    </Td>
                    <Td>
                      <div
                        style={{ display: "flex", justifyContent: "center" }}
                      >
                        {/*item.paymentStatus === PAYMENT_STATUS.PAID ?
                                                <ButtonConfirm onClick={() => handlePay(item.id)} style={{ cursor: 'pointer' }} />
                                            : <ButtonConfirmDisabled />
                                            */}
                                            {(role === ROLES.SUPER_ADMIN && item.invoiceStatus === INVOICE_STATUS.IN_PROGRESS) ?
                                            <ButtonView onClick={() => handleConfirmInvoice(item.id)} style={{ cursor: 'pointer' }} /> : null}
                                            {(role === ROLES.FINANCE) ?
                                            <Button
                                                isDisabled={item.paymentStatus !== PAYMENT_STATUS.UNPAID || item.invoiceStatus !== INVOICE_STATUS.APPROVED}
                                                bgColor='#079455' borderRadius='full' color='white' height='25px' onClick={() => handlePay(item.id)}
                                            >
                                                Pay
                                            </Button> : null}
                                            {(role === ROLES.INSPECTING_COMPANY && item.paymentStatus === PAYMENT_STATUS.PAID) ?
                                                <ButtonConfirm onClick={() => handleConfirmPayment(item.id)} style={{ cursor: 'pointer' }} />
                                            : null}
                                        </div>
                                    </Td>
                                    <Td>{item.notes || '-'}</Td>
                                    <Td style={{ display: 'flex', cursor: 'pointer', gap: 8, paddingTop: '26px', paddingBottom: '26px', justifyContent: 'center' }}>
                                        {[ROLES.INSPECTING_COMPANY, ROLES.SUPER_ADMIN].includes(role) && (item.invoiceStatus === 0 || item.invoiceStatus === 3) ? <>

                                            <EditIcon onClick={() => handleEdit(item.id)} />
                                            <DeleteIcon onClick={() => handleDelete(item.id)} />
                                        </> : '-'}
                                    </Td>
                                </Tr>
                            )
                        })}
                    </Tbody>
                </Table>
            </Box>
            <Pagination currentPageSize={currentPageSize} currentPageNumber={currentPageNumber} totalPageNumber={totalPageNumber} handleChangePageSize={handleChangePageSize} handleChangePageNumber={handleChangePageNumber} hasNextPage={hasNextPage} />
        </TableContainer>
    )
}

export default TableInvoiceSireReviewInspector;
